import { insertCallout } from "@platejs/callout";
import { insertCodeBlock } from "@platejs/code-block";
import { insertMedia } from "@platejs/media";
import { TablePlugin } from "@platejs/table/react";
import { insertToc } from "@platejs/toc";
import { KEYS, type NodeEntry, type Path, PathApi, type TElement } from "platejs";
import type { PlateEditor } from "platejs/react";

const insertList = (editor: PlateEditor, type: string) => {
	editor.tf.insertNodes(
		editor.api.create.block({
			indent: 1,
			listStyleType: type,
		}),
		{ select: true },
	);
};

const insertBlockMap: Record<string, (editor: PlateEditor, type: string) => void> = {
	[KEYS.listTodo]: insertList,
	[KEYS.ol]: insertList,
	[KEYS.ul]: insertList,
	[KEYS.callout]: (editor) => insertCallout(editor, { select: true }),
	[KEYS.codeBlock]: (editor) => insertCodeBlock(editor, { select: true }),
	[KEYS.img]: (editor) =>
		insertMedia(editor, {
			select: true,
			type: KEYS.img,
		}),
	[KEYS.table]: (editor) => editor.getTransforms(TablePlugin).insert.table({}, { select: true }),
	[KEYS.toc]: (editor) => insertToc(editor, { select: true }),
};

type InsertBlockOptions = {
	upsert?: boolean;
};

export const insertBlock = (
	editor: PlateEditor,
	type: string,
	options: InsertBlockOptions = {},
) => {
	const { upsert = false } = options;

	editor.tf.withoutNormalizing(() => {
		const block = editor.api.block();

		if (!block) return;

		const [currentNode, path] = block;
		const isCurrentBlockEmpty = editor.api.isEmpty(currentNode);
		const currentBlockType = getBlockType(currentNode);
		const isSameBlockType = type === currentBlockType;

		if (upsert && isCurrentBlockEmpty && isSameBlockType) {
			return;
		}

		if (type in insertBlockMap) {
			insertBlockMap[type]!(editor, type);
		} else {
			editor.tf.insertNodes(editor.api.create.block({ type }), {
				at: PathApi.next(path),
				select: true,
			});
		}

		if (!isSameBlockType) {
			editor.tf.removeNodes({ previousEmptyBlock: true });
		}
	});
};

const setList = (editor: PlateEditor, type: string, entry: NodeEntry<TElement>) => {
	editor.tf.setNodes(
		editor.api.create.block({
			indent: 1,
			listStyleType: type,
		}),
		{ at: entry[1] },
	);
};

const setBlockMap: Record<
	string,
	(editor: PlateEditor, type: string, entry: NodeEntry<TElement>) => void
> = {
	[KEYS.listTodo]: setList,
	[KEYS.ol]: setList,
	[KEYS.ul]: setList,
};

export const setBlockType = (editor: PlateEditor, type: string, { at }: { at?: Path } = {}) => {
	editor.tf.withoutNormalizing(() => {
		const setEntry = (entry: NodeEntry<TElement>) => {
			const [node, path] = entry;

			if (node[KEYS.listType]) {
				editor.tf.unsetNodes([KEYS.listType, "indent"], { at: path });
			}
			if (type in setBlockMap) {
				return setBlockMap[type]!(editor, type, entry);
			}
			if (node.type !== type) {
				editor.tf.setNodes({ type }, { at: path });
			}
		};

		if (at) {
			const entry: NodeEntry<TElement> | undefined = editor.api.node(at);
			if (entry) {
				setEntry(entry);
				return;
			}
		}

		const entries: NodeEntry<TElement>[] = editor.api.blocks({ mode: "lowest" });
		for (const entry of entries) setEntry(entry);
	});
};

export const getBlockType = (block: TElement) => {
	if (block[KEYS.listType]) {
		if (block[KEYS.listType] === KEYS.ol) return KEYS.ol;
		if (block[KEYS.listType] === KEYS.listTodo) return KEYS.listTodo;
		return KEYS.ul;
	}
	return block.type;
};
